/**
 * CookieConsent — GDPR/UK PECR-compliant cookie banner.
 *
 * Surfaces a one-time consent prompt to every visitor. Choices:
 *   - Accept all   — strictly necessary + analytics + marketing cookies
 *   - Reject all   — strictly necessary only (transactional cookies always work)
 *   - Customize    — granular toggle per category (functional / analytics / marketing)
 *
 * Preference is persisted to localStorage under `companin_cookie_consent_v1`
 * with the shape: { ts, version, choices: { necessary: true, functional: bool,
 * analytics: bool, marketing: bool } }. Other parts of the app can read
 * `getCookieConsent()` to gate optional integrations (PostHog, ads pixels,
 * etc.). PostHog opt-in is wired by default in this file.
 *
 * Hidden once the user has made a choice. Settings link in the footer
 * re-opens the dialog.
 */
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "companin_cookie_consent_v1";
const VERSION = 1;

const DEFAULT_CHOICES = {
  necessary: true,    // always on — strictly necessary
  functional: true,   // preferences (currency, theme, last-viewed)
  analytics: false,
  marketing: false,
};

export function getCookieConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.version !== VERSION) return null;
    return parsed.choices;
  } catch {
    return null;
  }
}

function saveConsent(choices) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ts: new Date().toISOString(), version: VERSION, choices }),
    );
    // Broadcast for in-app listeners (e.g. PostHog wrapper).
    window.dispatchEvent(new CustomEvent("companin:consent-changed", { detail: choices }));
  } catch {
    /* swallow */
  }
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [choices, setChoices] = useState(DEFAULT_CHOICES);

  useEffect(() => {
    const stored = getCookieConsent();
    if (stored) {
      setChoices(stored);
      setVisible(false);
    } else {
      setVisible(true);
    }
    // Allow other parts of the app (Footer "Cookie settings" link) to
    // re-open the banner via a custom event.
    const reopen = () => { setVisible(true); setShowCustom(true); };
    window.addEventListener("companin:open-cookie-settings", reopen);
    return () => window.removeEventListener("companin:open-cookie-settings", reopen);
  }, []);

  const finalize = (next) => {
    setChoices(next);
    saveConsent(next);
    setVisible(false);
    setShowCustom(false);
  };

  const acceptAll = () => finalize({ ...DEFAULT_CHOICES, analytics: true, marketing: true });
  const rejectAll = () => finalize({ ...DEFAULT_CHOICES, functional: true, analytics: false, marketing: false });
  const saveCustom = () => finalize(choices);

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 inset-x-4 md:bottom-6 md:left-6 md:right-auto md:max-w-md z-[60] rounded-2xl border border-stone-200 bg-white shadow-xl p-5"
      role="dialog"
      aria-label="Cookie consent"
      data-testid="cookie-consent-banner"
    >
      <div className="flex items-start gap-3">
        <div className="rounded-full bg-amber-100 p-2 shrink-0">
          <Cookie className="h-4 w-4 text-amber-700" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-stone-900">We respect your privacy</p>
          <p className="text-sm text-stone-600 mt-1 leading-relaxed">
            We use strictly-necessary cookies to run the platform and, with
            your consent, analytics + marketing cookies to improve your
            experience. Read our{" "}
            <a href="/privacy" className="text-[#4A5D4E] underline">privacy policy</a>.
          </p>

          {showCustom && (
            <div className="mt-4 space-y-3" data-testid="cookie-consent-custom">
              <CategoryRow
                label="Strictly necessary"
                description="Sign-in, security, booking checkout — always on."
                checked
                disabled
              />
              <CategoryRow
                label="Functional"
                description="Remembers your currency, last city, theme."
                checked={choices.functional}
                onChange={(v) => setChoices({ ...choices, functional: v })}
                testid="cookie-toggle-functional"
              />
              <CategoryRow
                label="Analytics"
                description="Anonymous usage stats to help us improve."
                checked={choices.analytics}
                onChange={(v) => setChoices({ ...choices, analytics: v })}
                testid="cookie-toggle-analytics"
              />
              <CategoryRow
                label="Marketing"
                description="Personalised offers + retargeting on partner sites."
                checked={choices.marketing}
                onChange={(v) => setChoices({ ...choices, marketing: v })}
                testid="cookie-toggle-marketing"
              />
            </div>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-2">
            {!showCustom && (
              <button
                onClick={() => setShowCustom(true)}
                className="text-xs text-stone-500 hover:text-stone-700 underline"
                data-testid="cookie-customize"
              >
                Customize
              </button>
            )}
            <div className="ml-auto flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={rejectAll}
                className="rounded-full"
                data-testid="cookie-reject-all"
              >
                Reject all
              </Button>
              {showCustom ? (
                <Button
                  size="sm"
                  onClick={saveCustom}
                  className="rounded-full bg-[#4A5D4E] hover:bg-[#3a4d3e]"
                  data-testid="cookie-save-custom"
                >
                  Save choices
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={acceptAll}
                  className="rounded-full bg-[#4A5D4E] hover:bg-[#3a4d3e]"
                  data-testid="cookie-accept-all"
                >
                  Accept all
                </Button>
              )}
            </div>
          </div>
        </div>
        <button
          onClick={rejectAll}
          className="text-stone-400 hover:text-stone-600 shrink-0"
          aria-label="Close (rejects optional cookies)"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function CategoryRow({ label, description, checked, onChange, disabled, testid }) {
  return (
    <label className="flex items-start justify-between gap-3 cursor-pointer">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-stone-900">{label}</div>
        <div className="text-[11px] text-stone-500 leading-tight">{description}</div>
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onChange}
        disabled={disabled}
        data-testid={testid}
      />
    </label>
  );
}
